export type CourseScheduleMode = "self_paced" | "relative_daily" | "shared_daily";
export interface CourseSchedule {
  mode: CourseScheduleMode;
  timezone: string;
  releaseHour: number;
  startDate: string;
}
export interface CourseScheduleDetails {
  schedule_mode?: CourseScheduleMode;
  schedule_timezone?: string;
  schedule_release_hour?: number;
  schedule_start_date?: string;
}
export interface CourseAvailability {
  unit_id: string;
  day_number: number;
  sort_order: number;
  lesson_title: string | null;
  unit_type: string;
  available_at: string | null;
  is_available: boolean;
  schedule_locked: boolean;
}
export function courseScheduleFromConfig(config: unknown): CourseSchedule {
  const value = (config && typeof config === "object" && "schedule" in config
    ? config.schedule : null) as Record<string, unknown> | null;
  return {
    mode: value?.mode === "relative_daily" || value?.mode === "shared_daily" ? value.mode : "self_paced",
    timezone: typeof value?.timezone === "string" ? value.timezone : "UTC",
    releaseHour: typeof value?.release_hour === "number" ? value.release_hour : 9,
    startDate: typeof value?.start_date === "string" ? value.start_date : "",
  };
}
export function courseScheduleDetails(config: unknown): CourseScheduleDetails {
  const schedule = courseScheduleFromConfig(config);
  return { schedule_mode: schedule.mode, schedule_timezone: schedule.timezone,
    schedule_release_hour: schedule.releaseHour, schedule_start_date: schedule.startDate };
}
export function validateCourseSchedule(details: CourseScheduleDetails): string | null {
  const mode = details.schedule_mode ?? "self_paced";
  if (!["self_paced", "relative_daily", "shared_daily"].includes(mode)) return "Choose a session schedule.";
  if (mode === "self_paced") return null;
  try { new Intl.DateTimeFormat("en", { timeZone: details.schedule_timezone || "" }).format(); }
  catch { return "Choose a valid time zone."; }
  if (!Number.isInteger(details.schedule_release_hour) || details.schedule_release_hour! < 0 || details.schedule_release_hour! > 23)
    return "Choose the hour sessions open.";
  if (mode === "shared_daily") {
    const date = details.schedule_start_date || "";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(`${date}T00:00:00Z`))
      || new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) !== date) return "Choose a valid start date.";
  }
  return null;
}
export function courseAvailabilityLabel(row: Pick<CourseAvailability, "is_available" | "available_at">, timezone: string): string {
  if (row.is_available) return "Available now";
  if (!row.available_at) return "Available after you join";
  try {
    return `Opens ${new Intl.DateTimeFormat(undefined, { timeZone: timezone, month: "short", day: "numeric", hour: "numeric", timeZoneName: "short" }).format(new Date(row.available_at))}`;
  } catch { return "Not available yet"; }
}

export function courseScheduleSummary(schedule: CourseSchedule): string {
  if (schedule.mode === "self_paced") return "All sessions are available when someone joins.";
  const time = `${String(schedule.releaseHour).padStart(2, "0")}:00 (${schedule.timezone.replace(/_/g, " ")})`;
  return schedule.mode === "relative_daily"
    ? `The first session opens when someone joins. The next opens the following day at ${time}.`
    : `Sessions open daily from ${schedule.startDate} at ${time}. People who join later can catch up.`;
}
